/**
 * Pluggable collision strategies for the pointer sensor. Pure geometry over
 * `Rect`-like inputs — no DOM — so they unit-test with plain objects.
 *
 * `pointerWithin` is the default and the right call for distinct drop *buckets*
 * (the settings phase sections): a bucket either contains the pointer or it
 * doesn't, which is unambiguous and immune to the "nearest center grabbed the
 * wrong neighbour" failure mode. `closestCenter` is for sortable lists where
 * every slot is adjacent and "containment" is meaningless — it ships now so the
 * kanban consumer has it without touching the package later.
 */
import type { CollisionDetection, Rect } from './types.ts'

function pointInRect(p: { x: number; y: number }, r: Rect): boolean {
  return p.x >= r.left && p.x <= r.left + r.width && p.y >= r.top && p.y <= r.top + r.height
}

function center(r: Rect): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
}

/**
 * The droppable whose rect contains the pointer. On overlap (nested droppables)
 * the smallest-area one wins, so the innermost target is selected.
 */
export const pointerWithin: CollisionDetection = ({ pointer, droppables }) => {
  let best: { id: string; area: number } | null = null
  for (const d of droppables) {
    if (d.disabled) continue
    if (!pointInRect(pointer, d.rect)) continue
    const area = d.rect.width * d.rect.height
    if (best === null || area < best.area) best = { id: d.id, area }
  }
  return best?.id ?? null
}

/**
 * The droppable whose center is nearest the pointer (squared distance — no need
 * for the sqrt). Never returns `null` while any enabled droppable exists, which
 * is the sortable-list semantic: the pointer is always "over" some slot.
 */
export const closestCenter: CollisionDetection = ({ pointer, droppables }) => {
  let best: { id: string; dist: number } | null = null
  for (const d of droppables) {
    if (d.disabled) continue
    const c = center(d.rect)
    const dist = (c.x - pointer.x) ** 2 + (c.y - pointer.y) ** 2
    if (best === null || dist < best.dist) best = { id: d.id, dist }
  }
  return best?.id ?? null
}
